import { useState, useEffect } from 'react';
import {
  Theme,
  Drawer,
  useTranslation,
  useBreakpoint,
  ButtonSize,
  Button,
  ButtonWidth,
  ButtonStyles,
  Breakpoints,
  FAIcon,
  Tooltip,
  TooltipPreference,
  IconStyle,
} from '@nextgen-web-framework/all';
import { StorageService } from '@nextgen-web-framework/core';
import { ChannelConfigs, sharebarApi } from '@commerce-webcomponents/sdk';
import { SessionStorageKeys } from '../../utils/common';
import ShareModal from '../ShareModal/ShareModal';
import { SocialShareLinkProps } from './types/propTypes';
import styles from './SocialShareLink.module.scss';

const SocialShareLink = ({
  shareRestrictions,
  linkProps,
  isCustomUrl,
  shareTextMessage,
  customClass,
  buttonProps,
  shareDrawer,
  showDescription,
  shareDescriptionsContent,
  shareTitle = true,
}: SocialShareLinkProps) => {
  const [showError, setShowError] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const t = useTranslation();
  const viewPortType = useBreakpoint();
  const sessionstorage = StorageService.getSessionStorage();
  const isMobile = viewPortType === Breakpoints.XS || viewPortType === Breakpoints.SM;
  const [showShareModal, setShowShareModal] = useState(false);
  const shareLinkHandler = () => {
    setShowShareModal(true);
  };
  const [socialIconsVisibility, setSocialIconsVisibility] = useState<ChannelConfigs>({});
  const handleClose = () => {
    setShowShareModal(false);
  };

  const getSharebarConfigs = async () => {
    try {
      const channelConfigs = await sharebarApi.getChannelConfig();
      sessionstorage.store(SessionStorageKeys.SHARE_CHANNEL_CONFIG, channelConfigs);
      setSocialIconsVisibility(channelConfigs);
    } catch (err) {
      setShowError(true);
    }
  };
  useEffect(() => {
    if (shareRestrictions) {
      setShowTooltip(true);
    } else {
      const existingChannelConfig = sessionstorage.retrieve(SessionStorageKeys.SHARE_CHANNEL_CONFIG) as ChannelConfigs;
      if (existingChannelConfig) {
        setSocialIconsVisibility(existingChannelConfig);
      } else {
        getSharebarConfigs();
      }
    }
  }, []);
  // Return tooltip with restricted message if share restriction is true
  if (showTooltip) {
    return (
      <Tooltip
        iconName="arrow-up-from-square"
        theme={Theme.PURPLE}
        iconStyle={IconStyle.REGULAR}
        className={`${styles['sharebar-share-restrictions']}`}
        message={t('web.share.restrictions.message')}
        preference={TooltipPreference.TOP_LEFT}
      />
    );
  }
  const positionClass = buttonProps?.title ? 'share-modal-btn-position' : 'share-modal-link-position';
  return (
    <div className={styles['social-share-link-wrapper']}>
      {shareDrawer ? (
        <div className={`${styles['sharebar-drawer-wrapper']}`} data-testid="shareDrawer-test-id">
          <ShareModal
            hasError={showError}
            linkProps={linkProps}
            shareTextMessage={shareTextMessage}
            isCustomUrl={isCustomUrl}
            socialIconsVisibility={socialIconsVisibility}
            showDescription={showDescription}
            shareDescriptionContent={shareDescriptionsContent}
          />
        </div>
      ) : buttonProps?.title ? (
        <div className={`${styles['share-button']}`}>
          <Button
            title={buttonProps?.title}
            onClick={shareLinkHandler}
            theme={buttonProps?.theme ?? Theme.BLACK}
            buttonWidth={ButtonWidth.FULL_WIDTH}
            disabled={buttonProps?.disabled}
            size={viewPortType == Breakpoints.XS ? ButtonSize.SMALL : ButtonSize.MEDIUM}
            buttonStyle={ButtonStyles.SOLID}
          />
        </div>
      ) : (
        <div className={styles['social-share-link']}>
          <FAIcon
            iconName="arrow-up-from-square"
            className={`${styles['share-icon']}`}
            onClick={shareLinkHandler}
            data-testid="shareIcon-test-id"
          />
        </div>
      )}
      {showShareModal &&
        (isMobile ? (
          <Drawer
            bottomGap={0}
            header={<div>{t('web.share.title')}</div>}
            closeIcon={true}
            closeOnBackdropClick={true}
            onClose={handleClose}
          >
            <div className={styles['share-modal-mobile-wrapper']}>
              <ShareModal
                hasError={showError}
                linkProps={linkProps}
                shareTextMessage={shareTextMessage}
                isCustomUrl={isCustomUrl || false}
                socialIconsVisibility={socialIconsVisibility}
                showDescription={showDescription}
                shareDescriptionContent={shareDescriptionsContent}
              />
            </div>
          </Drawer>
        ) : (
          <div
            className={`${styles['social-share-modal-wrapper']} ${styles[positionClass]} ${customClass}`}
            data-testid="share-modal-test-id"
          >
            <div>
              <FAIcon
                iconName="xmark"
                className={`${styles['sharebar-close-button']}`}
                onClick={handleClose}
                data-testid="closeButton-test-id"
              />
              {shareTitle && <span className={`${styles['sharebar-subheader']}`}>{t('web.share.title')}</span>}
            </div>
            <ShareModal
              hasError={showError}
              linkProps={linkProps}
              shareTextMessage={shareTextMessage}
              isCustomUrl={isCustomUrl || false}
              socialIconsVisibility={socialIconsVisibility}
              showDescription={showDescription}
              shareDescriptionContent={shareDescriptionsContent}
            />
          </div>
        ))}
    </div>
  );
};

export { SocialShareLink };
